import type { KryspaceRenderManifest } from '../../types/kryspaceRenderManifest';
import type { ControlBridgeQueueItem, ControlBridgeViewModel } from './deriveViewModel';
import type { ControlBridgeSurfaceKey } from './deriveViewModel';
import type { ResourceFilter, ScopeContext } from './filters';
import { assigneeIdentityKey, passesFilter } from './filters';
import type { PersonalOverlayMode } from './personalLayer';
import type { PersonalLayerUserContext } from './personalLayer/buildPersonalLayerViewModels';

export type RecommendedSurfaceKey = 'exopanel' | 'drive' | 'guidance' | 'diagnose';

export interface AttentionRoutingInputs {
  queueCount: number;
  blockedCount: number;
  unassignedCount: number;
  entropyVsThreshold: boolean;
  overallEntropy: number | null;
}

export interface AttentionRouting {
  recommendedSurface: RecommendedSurfaceKey;
  reason: string;
  inputs: AttentionRoutingInputs;
  source: 'manifest' | 'derived';
}

const ENTROPY_ROUTING_THRESHOLD = 7;

const SURFACE_REASONS: Record<RecommendedSurfaceKey, string> = {
  exopanel: 'Work ready to act — open Exo Panel',
  drive: 'Flow blocked — review operational flow',
  guidance: 'No ready work or unassigned activities — address planning',
  diagnose: 'Entropy above threshold — create leverage',
};

export const RECOMMENDED_SURFACE_LABELS: Record<RecommendedSurfaceKey, string> = {
  exopanel: 'Exo Panel',
  drive: 'Drive',
  guidance: 'Guidance',
  diagnose: 'Diagnostics',
};

function routingField(
  manifest: KryspaceRenderManifest,
  key: string
): string | number | boolean | null {
  const section = manifest.sections.find((s) => s.id === 'attention-routing');
  const field = section?.fields?.find((f) => f.key === key);
  return field?.value ?? null;
}

function routingMetaInputs(manifest: KryspaceRenderManifest): Partial<AttentionRoutingInputs> | null {
  const section = manifest.sections.find((s) => s.id === 'attention-routing');
  const meta = section?.meta as { routingInputs?: Record<string, unknown> } | undefined;
  const raw = meta?.routingInputs;
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  return {
    queueCount: typeof raw.queueCount === 'number' ? raw.queueCount : undefined,
    blockedCount: typeof raw.blockedCount === 'number' ? raw.blockedCount : undefined,
    unassignedCount: typeof raw.unassignedCount === 'number' ? raw.unassignedCount : undefined,
    entropyVsThreshold:
      typeof raw.entropyVsThreshold === 'boolean' ? raw.entropyVsThreshold : undefined,
    overallEntropy:
      typeof raw.overallEntropy === 'number'
        ? raw.overallEntropy
        : raw.overallEntropy === null
          ? null
          : undefined,
  };
}

function parseRecommendedSurface(value: unknown): RecommendedSurfaceKey | null {
  if (value === 'exopanel' || value === 'drive' || value === 'guidance' || value === 'diagnose') {
    return value;
  }
  return null;
}

export function routingInputsFromManifest(manifest: KryspaceRenderManifest): AttentionRoutingInputs | null {
  const surface = parseRecommendedSurface(routingField(manifest, 'recommended_surface'));
  if (!surface) {
    return null;
  }
  const meta = routingMetaInputs(manifest);
  const queueRaw = routingField(manifest, 'queue_count');
  const blockedRaw = routingField(manifest, 'blocked_count');
  const unassignedRaw = routingField(manifest, 'unassigned_count');
  const entropyFlag = routingField(manifest, 'entropy_vs_threshold');
  const entropyRaw = routingField(manifest, 'overall_entropy');
  return {
    queueCount: meta?.queueCount ?? (typeof queueRaw === 'number' ? queueRaw : 0),
    blockedCount: meta?.blockedCount ?? (typeof blockedRaw === 'number' ? blockedRaw : 0),
    unassignedCount: meta?.unassignedCount ?? (typeof unassignedRaw === 'number' ? unassignedRaw : 0),
    entropyVsThreshold:
      meta?.entropyVsThreshold ?? (typeof entropyFlag === 'boolean' ? entropyFlag : false),
    overallEntropy:
      meta?.overallEntropy ??
      (typeof entropyRaw === 'number'
        ? entropyRaw
        : typeof entropyRaw === 'string' && entropyRaw.trim()
          ? (() => {
              const n = Number(entropyRaw);
              return Number.isFinite(n) ? n : null;
            })()
          : null),
  };
}

export function parseAttentionRoutingFromManifest(
  manifest: KryspaceRenderManifest
): AttentionRouting | null {
  const section = manifest.sections.find((s) => s.id === 'attention-routing');
  if (!section) {
    return null;
  }
  const surface = parseRecommendedSurface(routingField(manifest, 'recommended_surface'));
  if (!surface) {
    return null;
  }
  const reasonRaw = routingField(manifest, 'reason');
  const inputs = routingInputsFromManifest(manifest);
  if (!inputs) {
    return null;
  }
  return {
    recommendedSurface: surface,
    reason: typeof reasonRaw === 'string' && reasonRaw.trim() ? reasonRaw : SURFACE_REASONS[surface],
    inputs,
    source: 'manifest',
  };
}

function normalizeAssigneeRef(ref: string | undefined | null): string | undefined {
  if (!ref) return undefined;
  const text = ref.trim();
  if (!text) return undefined;
  const bare = text.startsWith('person:') ? text.slice(7).trim() : text;
  return bare ? bare.toLowerCase() : undefined;
}

export function isAssignedToUser(item: ControlBridgeQueueItem, userRef?: string): boolean {
  const want = normalizeAssigneeRef(userRef);
  if (!want) {
    return false;
  }
  const assigned = normalizeAssigneeRef(item.assignedResourceRef);
  if (assigned && assigned === want) {
    return true;
  }
  // Fallback: some rows only carry the filter token on `res`
  const res = normalizeAssigneeRef(item.res);
  if (res && res === want) {
    return true;
  }
  // Same plane as person-chip matching (co-prh-steve ≡ ai-op-resource-profile-steve ≡ steve).
  const wantId = assigneeIdentityKey(want);
  if (!wantId) {
    return false;
  }
  const candidates = [item.assignedResourceRef, item.res, item.assignedResourceLabel];
  return candidates.some((c) => {
    const gotId = assigneeIdentityKey(c);
    return Boolean(gotId && gotId === wantId);
  });
}

export function isUnassignedItem(item: ControlBridgeQueueItem): boolean {
  return !String(item.assignedResourceRef ?? item.assignedResourceLabel ?? '').trim();
}

export function filterEligibleForRoutingContext(
  items: ControlBridgeQueueItem[],
  scope: string,
  resource: ResourceFilter,
  scopeContext: ScopeContext | undefined,
  overlayMode: PersonalOverlayMode,
  user?: PersonalLayerUserContext
): ControlBridgeQueueItem[] {
  const scoped = items.filter((item) => passesFilter(item, scope, resource, scopeContext));
  if (overlayMode !== 'me' || !user?.id?.trim()) {
    return scoped;
  }
  // A person resource chip already encodes assignee; do not also require Me.
  if (resource.startsWith('person:')) {
    return scoped;
  }
  return scoped.filter((item) => isAssignedToUser(item, user.id));
}

export function buildRoutingInputsFromQueue(
  eligibleWork: ControlBridgeQueueItem[],
  blockedCount: number,
  overallEntropy: number | null
): AttentionRoutingInputs {
  const unassignedCount = eligibleWork.filter(isUnassignedItem).length;
  return {
    queueCount: eligibleWork.length,
    blockedCount,
    unassignedCount,
    entropyVsThreshold:
      overallEntropy != null && Number.isFinite(overallEntropy)
        ? overallEntropy >= ENTROPY_ROUTING_THRESHOLD
        : false,
    overallEntropy,
  };
}

export function resolveRecommendedSurface(inputs: AttentionRoutingInputs): RecommendedSurfaceKey {
  if (inputs.queueCount > 0) {
    return 'exopanel';
  }
  if (inputs.blockedCount > 0) {
    return 'drive';
  }
  if (inputs.entropyVsThreshold) {
    return 'diagnose';
  }
  if (inputs.queueCount === 0 || inputs.unassignedCount > 0) {
    return 'guidance';
  }
  return 'guidance';
}

export function deriveAttentionRoutingFallback(vm: ControlBridgeViewModel): AttentionRouting {
  const inputs = buildRoutingInputsFromQueue(
    vm.eligibleWork,
    vm.blockedActivityCount,
    vm.overallEntropy
  );
  const recommendedSurface = resolveRecommendedSurface(inputs);
  return {
    recommendedSurface,
    reason: SURFACE_REASONS[recommendedSurface],
    inputs,
    source: 'derived',
  };
}

export interface UserScopedRoutingContext {
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  overlayMode: PersonalOverlayMode;
  user?: PersonalLayerUserContext;
}

export function applyUserScopedAttentionRouting(
  base: AttentionRouting,
  vm: ControlBridgeViewModel,
  ctx: UserScopedRoutingContext
): AttentionRouting {
  const useMeScope =
    ctx.overlayMode === 'me' && Boolean(ctx.user?.id?.trim()) && ctx.resource === 'all';
  if (!useMeScope) {
    return base;
  }

  const myEligible = filterEligibleForRoutingContext(
    vm.eligibleWork,
    ctx.scope,
    ctx.resource,
    ctx.scopeContext,
    ctx.overlayMode,
    ctx.user
  );
  const allEligible = vm.eligibleWork.filter((item) =>
    passesFilter(item, ctx.scope, ctx.resource, ctx.scopeContext)
  );
  const inputs: AttentionRoutingInputs = {
    ...base.inputs,
    queueCount: myEligible.length,
    unassignedCount: allEligible.filter(isUnassignedItem).length,
  };
  const recommendedSurface = resolveRecommendedSurface(inputs);
  return {
    recommendedSurface,
    reason: SURFACE_REASONS[recommendedSurface],
    inputs,
    source: base.source === 'manifest' ? 'manifest' : 'derived',
  };
}

export function deriveAttentionRouting(
  manifest: KryspaceRenderManifest,
  vm: ControlBridgeViewModel,
  ctx?: UserScopedRoutingContext
): AttentionRouting {
  const base = parseAttentionRoutingFromManifest(manifest) ?? deriveAttentionRoutingFallback(vm);
  if (!ctx) {
    return base;
  }
  return applyUserScopedAttentionRouting(base, vm, ctx);
}
